import React, { FC, useState, useEffect, useCallback } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import PreviewTemplate from 'components/common/previewTemplate';
import { useCreateBusinessUnitTemplateMutation, useUploadTemplateFileMutation } from 'services/api/businessUnits.api';
import type { TemplateCreate, WhatsAppButton } from 'services/api/businessUnits.api';

// Common emojis matrix
const COMMON_EMOJIS = [
  ['😊', '👍', '🎉', '❤️', '🙏', '✨', '💪', '🤝'],
  ['👋', '🌟', '💯', '🔥', '👏', '🎯', '✅', '🚗'],
  ['📱', '💼', '📊', '📈', '🎁', '🛒', '💰', '📝'],
  ['🔔', '⏰', '📅', '🌈', '🚀', '🏆', '💡', '🔍'],
];
interface Props {
  businessUnitId: number;
}

const MAX_BUTTON_LENGTH = 25;
const MAX_VARIABLE_LENGTH = 25;
const MAX_FOOTER_LENGTH = 60;

const validateBodyVariables = (text: string): boolean => {
  // Count variables in text
  const variables = text.match(/{{[1-9]}}/g) || [];
  if (variables.length === 0) return true;

  // Count total words in the text
  const words = text.trim().split(/\s+/);

  // Need at least 4 words per variable
  return words.length >= variables.length * 4;
};

const CreateTemplateForm: FC<Props> = ({ businessUnitId }) => {
  const [createTemplate, { isLoading, isSuccess, isError }] = useCreateBusinessUnitTemplateMutation();
  const [uploadTemplateFile] = useUploadTemplateFileMutation();

  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState<'UTILITY' | 'MARKETING'>('MARKETING');
  const language = 'es';

  const [headerEnabled, setHeaderEnabled] = useState(false);
  const [headerFormat, setHeaderFormat] = useState<'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT'>('TEXT');
  const [headerFile, setHeaderFile] = useState<File | null>(null);
  const [headerText, setHeaderText] = useState('');
  const [headerFileUrl, setHeaderFileUrl] = useState<string | null>(null);

  const [bodyText, setBodyText] = useState('');
  const [bodyVariableExamples, setBodyVariableExamples] = useState<string[]>([]);

  const [footerEnabled, setFooterEnabled] = useState(false);
  const [footerText, setFooterText] = useState('');

  const [buttons, setButtons] = useState<WhatsAppButton[]>([{ type: 'QUICK_REPLY', text: 'Desactivar marketing' }]);

  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(null);

  const transformTemplateName = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9_]/g, '_');
  };

  const addVariable = () => {
    const currentVarCount = (bodyText.match(/{{[1-9]}}/g) || []).length;
    const bodyInput = document.querySelector('textarea[name="BODY"]') as HTMLTextAreaElement;

    const cursorPosition = bodyInput.selectionStart;
    const newVariable = `{{${currentVarCount + 1}}}`;
    const newText = bodyText.slice(0, cursorPosition) + newVariable + bodyText.slice(cursorPosition);
    setBodyText(newText);
    setBodyVariableExamples([...bodyVariableExamples, '']);

    // Focus the input again after adding a variable
    setTimeout(() => {
      if (bodyInput) {
        bodyInput.focus();
        const newCursorPosition = cursorPosition + newVariable.length;
        bodyInput.selectionStart = newCursorPosition;
        bodyInput.selectionEnd = newCursorPosition;
      }
    }, 0);
  };

  const handleExampleChange = (index: number, value: string) => {
    const newExamples = [...bodyVariableExamples];
    newExamples[index] = value;
    setBodyVariableExamples(newExamples);
  };

  const removeVariable = useCallback(
    (indexToRemove: number) => {
      // Remove the variable from the text and adjust remaining variables
      let newText = bodyText.replaceAll(`{{${indexToRemove + 1}}}`, '');

      // Adjust the remaining variable numbers
      for (let i = indexToRemove + 1; i <= bodyVariableExamples.length - 1; i += 1) {
        newText = newText.replaceAll(`{{${i + 1}}}`, `{{${i}}}`);
      }
      // Remove the last variable
      newText = newText.replaceAll(`{{${bodyVariableExamples.length}}}`, '');

      setBodyText(newText);

      // Remove the example and update state
      const newExamples = bodyVariableExamples.filter((_, index) => index !== indexToRemove);
      setBodyVariableExamples(newExamples);
    },
    [bodyText, setBodyText, bodyVariableExamples, setBodyVariableExamples]
  );

  const handleNewTemplateSubmit = () => {
    const template: TemplateCreate = {
      name: templateName,
      category: templateCategory,
      language,
      components: [],
    };

    if (headerEnabled) {
      template.components.push({
        type: 'HEADER',
        text: headerFormat === 'TEXT' ? headerText : undefined,
        format: headerFormat,
        example:
          headerFormat !== 'TEXT'
            ? {
                header_handle: [headerFileUrl as string],
              }
            : undefined,
      });
    }

    template.components.push({
      type: 'BODY',
      text: bodyText,
      example:
        bodyVariableExamples.length > 0
          ? {
              body_text: [bodyVariableExamples],
            }
          : undefined,
    });

    if (footerEnabled) {
      template.components.push({
        type: 'FOOTER',
        text: footerText,
      });
    }

    if (buttons.length > 0) {
      template.components.push({
        type: 'BUTTONS',
        buttons,
      });
    }

    createTemplate({
      businessUnitId,
      template,
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error(err);
    });
  };

  // Handle template creation success
  useEffect(() => {
    if (!isSuccess) return () => {};

    setShowSuccess(true);
    const timer = setTimeout(() => {
      window.location.reload();
    }, 15000);

    return () => clearTimeout(timer);
  }, [isSuccess]);

  // Handle template creation error
  useEffect(() => {
    if (isError) {
      // eslint-disable-next-line no-console
      console.error('Error creating template');
      setShowError(true);
    }
  }, [isError]);

  let isFormValid = true;
  if (
    !templateName ||
    !templateName.match(/^[a-z0-9_]{5,40}$/) ||
    !bodyText ||
    !validateBodyVariables(bodyText) ||
    bodyVariableExamples.some((example) => !example) ||
    buttons.some((button) => !button.text) ||
    (headerEnabled && headerFormat === 'TEXT' && !headerText) ||
    (headerEnabled && headerFormat !== 'TEXT' && !headerFileUrl) ||
    (footerEnabled && !footerText)
  ) {
    isFormValid = false;
  }

  const handleAddFormatting = useCallback(
    (char: string) => {
      const textarea = document.querySelector('textarea[name="BODY"]') as HTMLTextAreaElement;
      if (!textarea) {
        return;
      }
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const newText = text.substring(0, start) + char + text.substring(start, end) + char + text.substring(end);

      setBodyText(newText);

      // Set cursor position between the formatting characters
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + 1, end + 1);
      }, 0);
    },
    [setBodyText]
  );

  const handleEmojiClick = useCallback(
    (emoji: string) => {
      const textarea = document.querySelector('textarea[name="BODY"]') as HTMLTextAreaElement;
      if (!textarea) {
        return;
      }
      const start = textarea.selectionStart;
      const text = textarea.value;
      const newText = text.substring(0, start) + emoji + text.substring(start);

      setBodyText(newText);

      // Set cursor position after the emoji
      setTimeout(() => {
        textarea.focus();
        const newPosition = start + emoji.length;
        textarea.setSelectionRange(newPosition, newPosition);
      }, 0);
      setEmojiAnchorEl(null);
    },
    [setBodyText]
  );

  const handleHeaderFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (headerFormat === 'IMAGE' && !file.type.startsWith('image/')) {
      setShowError(true);
      return;
    }
    if (headerFormat === 'DOCUMENT' && file.type !== 'application/pdf') {
      setShowError(true);
      return;
    }

    // Validate file size
    const maxSize = headerFormat === 'IMAGE' ? 1_000_000 : 5_000_000; // 1MB for images, 5MB for documents
    if (file.size > maxSize) {
      setShowError(true);
      return;
    }

    setHeaderFile(file);
    uploadTemplateFile({ businessUnitId, file })
      .unwrap()
      .then((response) => {
        setHeaderFileUrl(response.url);
      })
      .catch(() => {
        setShowError(true);
        setHeaderFile(null);
      });
  };

  const getFileValidationError = () => {
    if (!headerFile) return null;

    if (headerFormat === 'IMAGE') {
      if (!headerFile.type.startsWith('image/')) {
        return 'Solo se permiten archivos de imagen (JPEG, PNG)';
      }
      if (headerFile.size > 1024 * 1024) {
        return 'La imagen debe pesar menos de 1MB';
      }
    }

    if (headerFormat === 'DOCUMENT') {
      if (headerFile.type !== 'application/pdf') {
        return 'Solo se permiten archivos PDF';
      }
      if (headerFile.size > 5 * 1024 * 1024) {
        return 'El documento debe pesar menos de 5MB';
      }
    }

    return null;
  };

  return (
    <Box p={2} maxWidth={1400} mx="auto">
      <Snackbar
        open={showSuccess}
        autoHideDuration={15000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          border: '1px solid',
        }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }}>
          ¡Template creado exitosamente! El template será revisado por WhatsApp para su aprobación. Este proceso suele
          ser automático y rápido si es que cumple las políticas de WhatsApp. La página se actualizará en unos segundos.
        </Alert>
      </Snackbar>
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          border: '1px solid',
        }}
      >
        <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: '100%' }}>
          {getFileValidationError() || 'Ha ocurrido un error al subir el archivo. Por favor, inténtalo nuevamente.'}
        </Alert>
      </Snackbar>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <TextField
            autoFocus
            label="Nombre del template"
            value={templateName}
            onChange={(e) => setTemplateName(transformTemplateName(e.target.value))}
            required
            fullWidth
            sx={{ mb: 2 }}
            error={templateName.length < 5 || templateName.length > 40}
            helperText={
              templateName.length < 5 || templateName.length > 40
                ? 'El nombre debe tener entre 5 y 40 caracteres y solo puede contener letras minúsculas, números y guiones bajos'
                : ''
            }
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Categoría</InputLabel>
            <Select
              value={templateCategory}
              label="Categoría"
              onChange={(e) => setTemplateCategory(e.target.value as 'UTILITY' | 'MARKETING')}
            >
              <MenuItem value="UTILITY">Utility</MenuItem>
              <MenuItem value="MARKETING">Marketing</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Utility: Solo para temas específicos como recordatorios de visitas, encuesta de satisfacción, etc.
              <br />
              Marketing: Para la mayoría de los casos en que se busca convencer al cliente de comprar/contratar un
              servicio.
            </Typography>
          </FormControl>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              mb: 2,
            }}
          >
            <Box mb={2}>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="subtitle2">Encabezado (opcional)</Typography>
                <Switch
                  checked={headerEnabled}
                  onChange={(e) => setHeaderEnabled(e.target.checked)}
                  size="small"
                  sx={{ ml: 1 }}
                />
                {!headerEnabled && (
                  <Typography variant="body2" color="text.secondary">
                    Habilita el encabezado para agregar un título, imagen o pdf al inicio del mensaje.
                  </Typography>
                )}
              </Box>
              {headerEnabled && (
                <>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Formato</InputLabel>
                    <Select
                      value={headerFormat}
                      label="Formato"
                      onChange={(e) => {
                        const format = e.target.value as 'TEXT' | 'IMAGE' | 'DOCUMENT';
                        setHeaderFormat(format);
                        setHeaderFile(null);
                        setHeaderFileUrl(null);
                      }}
                    >
                      <MenuItem value="TEXT">Texto</MenuItem>
                      <MenuItem value="IMAGE">Imagen</MenuItem>
                      <MenuItem value="DOCUMENT">Documento</MenuItem>
                    </Select>
                  </FormControl>
                  {headerFormat === 'TEXT' ? (
                    <TextField
                      name="HEADER"
                      value={headerText}
                      onChange={(e) => setHeaderText(e.target.value)}
                      fullWidth
                      placeholder="Título"
                      helperText="Se verá en negrita y más grande al inicio del mensaje"
                    />
                  ) : (
                    <Box>
                      <input
                        type="file"
                        accept={headerFormat === 'IMAGE' ? 'image/png,image/jpeg,image/jpg' : '.pdf'}
                        onChange={handleHeaderFileChange}
                        style={{ marginBottom: '8px' }}
                      />
                      {headerFileUrl && (
                        <Box mt={1}>
                          {headerFormat === 'IMAGE' ? (
                            <img src={headerFileUrl} alt="Header" style={{ maxWidth: '200px' }} />
                          ) : (
                            <Typography variant="body2">Documento PDF cargado: {headerFile?.name}</Typography>
                          )}
                        </Box>
                      )}
                      {getFileValidationError() && (
                        <Typography color="error" variant="caption">
                          {getFileValidationError()}
                        </Typography>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
            <Box mb={2}>
              <Typography variant="subtitle2" mb={1}>
                Cuerpo del mensaje
              </Typography>
              <TextField
                multiline
                name="BODY"
                minRows={4}
                maxRows={10}
                value={bodyText}
                onChange={(e) => {
                  setBodyText(e.target.value);
                }}
                fullWidth
                placeholder="Escribe tu mensaje aquí"
                helperText={
                  !validateBodyVariables(bodyText) ? 'Debe haber al menos 4 palabras por cada variable en el texto' : ''
                }
                error={!validateBodyVariables(bodyText)}
              />
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                }}
              >
                <Tooltip title="Negrita">
                  <IconButton size="small" onClick={() => handleAddFormatting('*')}>
                    <FormatBoldIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cursiva">
                  <IconButton size="small" onClick={() => handleAddFormatting('_')}>
                    <FormatItalicIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Tachado">
                  <IconButton size="small" onClick={() => handleAddFormatting('~')}>
                    <FormatStrikethroughIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Emoji">
                  <IconButton size="small" onClick={(e) => setEmojiAnchorEl(e.currentTarget)}>
                    <InsertEmoticonIcon />
                  </IconButton>
                </Tooltip>
                <Popover
                  open={Boolean(emojiAnchorEl)}
                  anchorEl={emojiAnchorEl}
                  onClose={() => setEmojiAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                >
                  <Grid>
                    {COMMON_EMOJIS.map((row) => (
                      <Grid container item key={row[0]} spacing={1}>
                        {row.map((emoji) => (
                          <Grid item key={emoji}>
                            <IconButton
                              size="small"
                              onClick={() => handleEmojiClick(emoji)}
                              sx={{
                                fontSize: '1.5rem',
                              }}
                            >
                              {emoji}
                            </IconButton>
                          </Grid>
                        ))}
                      </Grid>
                    ))}
                  </Grid>
                </Popover>
                <Button
                  variant="outlined"
                  onClick={addVariable}
                  size="small"
                  startIcon={<AddIcon />}
                  sx={{ ml: 'auto' }}
                >
                  Agregar variable
                </Button>
              </Box>
              {bodyVariableExamples.length > 0 && (
                <Box mt={1.5}>
                  <Typography variant="subtitle2" fontWeight={500} mb={0.5}>
                    Ejemplos de variables:
                  </Typography>
                  <Stack spacing={1.5}>
                    {bodyVariableExamples.map((example, varIndex) => (
                      <Box key={`${varIndex + 1}`} display="flex" alignItems="flex-start" gap={1}>
                        <TextField
                          label={`Variable {{${varIndex + 1}}}`}
                          value={example}
                          onChange={(e) => {
                            const value = e.target.value.slice(0, MAX_VARIABLE_LENGTH);
                            handleExampleChange(varIndex, value);
                          }}
                          fullWidth
                          size="small"
                          inputProps={{ maxLength: MAX_VARIABLE_LENGTH }}
                          helperText={
                            !example ? 'El ejemplo no puede estar vacío' : `${example.length}/${MAX_VARIABLE_LENGTH}`
                          }
                          error={!example}
                        />
                        <Button onClick={() => removeVariable(varIndex)} color="error" sx={{ minWidth: 'auto' }}>
                          <DeleteIcon />
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>

            <Box mb={2}>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="subtitle2">Pie de página (opcional)</Typography>
                <Switch
                  checked={footerEnabled}
                  onChange={(e) => setFooterEnabled(e.target.checked)}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Box>
              {footerEnabled && (
                <TextField
                  value={footerText}
                  onChange={(e) => {
                    setFooterText(e.target.value);
                  }}
                  fullWidth
                  placeholder="Pie de página"
                  inputProps={{ maxLength: MAX_FOOTER_LENGTH }}
                  helperText={`Se verá en gris claro, sirve para poner el nombre de la empresa o persona. ${footerText.length}/${MAX_FOOTER_LENGTH}`}
                />
              )}
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={500} mb={1}>
                Botones
              </Typography>
              <Stack spacing={1}>
                {buttons.map((button, buttonIndex) => (
                  <Box key={`${buttonIndex + 1}`} display="flex" alignItems="flex-start" gap={1}>
                    <TextField
                      label={`Botón ${buttonIndex + 1}`}
                      value={button.text}
                      onChange={(e) => {
                        const newButtons = [...buttons];
                        newButtons[buttonIndex] = { type: 'QUICK_REPLY', text: e.target.value };
                        setButtons(newButtons);
                      }}
                      size="small"
                      fullWidth
                      inputProps={{ maxLength: MAX_BUTTON_LENGTH }}
                      helperText={`${button.text.length}/${MAX_BUTTON_LENGTH}`}
                    />
                    <Button
                      onClick={() => {
                        const newButtons = buttons.filter((_, i) => i !== buttonIndex);
                        setButtons(newButtons);
                      }}
                      color="error"
                      sx={{ minWidth: 'auto' }}
                    >
                      <DeleteIcon />
                    </Button>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  onClick={() => setButtons([...buttons, { type: 'QUICK_REPLY', text: '' }])}
                  startIcon={<AddIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                  }}
                  disabled={buttons.length >= 3}
                >
                  Agregar Botón
                </Button>
              </Stack>
            </Box>
          </Paper>

          <Button
            variant="contained"
            size="large"
            onClick={handleNewTemplateSubmit}
            disabled={!isFormValid || isLoading}
            sx={{
              mt: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
            }}
          >
            {isLoading ? 'Creando Template...' : 'Crear Template'}
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Después de crear el template, podrás utilizarlo para enviar mensajes a tus clientes desde la pestaña
            &quot;Enviar Template&quot;.
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <PreviewTemplate
            headerEnabled={headerEnabled}
            headerText={headerText}
            headerFormat={headerFormat}
            headerFileUrl={headerFileUrl}
            bodyText={bodyText}
            bodyVariableExamples={bodyVariableExamples}
            footerEnabled={footerEnabled}
            footerText={footerText}
            buttons={buttons}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CreateTemplateForm;
